import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useForm } from '@inertiajs/react'
import { FormEvent } from 'react'

interface Community {
  id: number
  name: string
}

interface FaqItemData {
  id?: number
  question: string
  answer: string
  community_id: number | string
  position: number
}

interface FaqItemFormProps {
  faqItem?: FaqItemData
  communities: Community[]
  maxPosition: number
  onSubmit: string
  isEdit?: boolean
}

export function FaqItemForm({
  faqItem,
  communities,
  maxPosition,
  onSubmit,
  isEdit = false
}: FaqItemFormProps) {
  const { data, setData, post, put, processing, errors } = useForm({
    question: faqItem?.question || '',
    answer: faqItem?.answer || '',
    community_id: faqItem?.community_id?.toString() || '',
    position: faqItem?.position || maxPosition
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isEdit && faqItem?.id) {
      put(onSubmit)
    } else {
      post(onSubmit)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card className="p-6">
        <div className="space-y-6">
          {/* Question */}
          <div>
            <label htmlFor="question" className="block text-sm font-medium text-text-dark mb-1">
              Question *
            </label>
            <Textarea
              id="question"
              value={data.question}
              onChange={(e) => setData('question', e.target.value)}
              placeholder="Ex: Comment puis-je modifier mon profil ?"
              rows={3}
              required
            />
            {errors.question && (
              <p className="mt-1 text-sm text-red-600">{errors.question}</p>
            )}
            <p className="mt-1 text-xs text-text-gray">
              Formulez la question de manière claire et concise.
            </p>
          </div>

          {/* Answer */}
          <div>
            <label htmlFor="answer" className="block text-sm font-medium text-text-dark mb-1">
              Réponse *
            </label>
            <Textarea
              id="answer"
              value={data.answer}
              onChange={(e) => setData('answer', e.target.value)}
              placeholder="Expliquez la réponse de manière détaillée..."
              rows={6}
              required
            />
            {errors.answer && (
              <p className="mt-1 text-sm text-red-600">{errors.answer}</p>
            )}
            <p className="mt-1 text-xs text-text-gray">
              Fournissez une réponse complète et utile aux utilisateurs.
            </p>
          </div>

          {/* Community */}
          <div>
            <label htmlFor="community_id" className="block text-sm font-medium text-text-dark mb-1">
              Communauté *
            </label>
            <Select
              id="community_id"
              value={data.community_id}
              onChange={(e) => setData('community_id', e.target.value)}
              required
            >
              <option value="">Sélectionner une communauté</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
            {errors.community_id && (
              <p className="mt-1 text-sm text-red-600">{errors.community_id}</p>
            )}
          </div>

          {/* Position */}
          <div>
            <label htmlFor="position" className="block text-sm font-medium text-text-dark mb-1">
              Position
            </label>
            <Input
              id="position"
              type="number"
              min={0}
              max={100}
              value={data.position}
              onChange={(e) => setData('position', parseInt(e.target.value) || 0)}
            />
            {errors.position && (
              <p className="mt-1 text-sm text-red-600">{errors.position}</p>
            )}
            <p className="mt-1 text-xs text-text-gray">
              Les questions sont triées par position (0 = premier). Valeur suggérée : {maxPosition}
            </p>
          </div>
        </div>

        {/* Submit */}
        <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-4">
          <Button type="button" variant="secondary" onClick={() => history.back()}>
            Annuler
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer la question'}
          </Button>
        </div>
      </Card>
    </form>
  )
}
